import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import StatusBadge from './common/StatusBadge';
import TagSelector from './TagSelector';

const AppointmentsPage = ({
  appointments,
  clients,
  teamMembers,
  orderFormSubmissions,
  serviceTypes,
  availableTags,
  appointmentFilters,
  showAppointmentFilters,
  setActiveTab,
  setAppointmentFilters,
  setShowAppointmentFilters,
  setCalendarMonth,
  setCalendarView,
  setOrderFormSubmissions,
  setAppointments,
  setSelectedPendingSubmission,
  setShowRejectModal,
  setRejectReason,
  setShowProposeTimeModal,
  setProposedDate,
  setProposedTime,
  setProposalNote,
  setShowAppointmentForm,
  setEditingAppointment,
  setAppointmentForm,
  setSelectedProducts,
  setAppointmentClientSearchQuery,
  hasPermission,
  formatTableTime,
  showToast,
  API,
  axios
}) => {
  const safeAppointments = Array.isArray(appointments) ? appointments : [];
  const safeClients = Array.isArray(clients) ? clients : [];
  const safeTags = Array.isArray(availableTags) ? availableTags : [];

  const [savedViews, setSavedViews] = useState([]);
  const [viewsLoaded, setViewsLoaded] = useState(false);
  const [activeViewId, setActiveViewId] = useState(null);
  const [showSaveViewInput, setShowSaveViewInput] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name, x, y }
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Load saved views from backend once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/me/saved-views/appointments`);
        if (!cancelled) {
          setSavedViews(Array.isArray(res.data?.views) ? res.data.views : []);
          setViewsLoaded(true);
        }
      } catch (err) {
        if (!cancelled) setViewsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Persist saved views to backend whenever they change (after initial load)
  useEffect(() => {
    if (!viewsLoaded) return;
    const timeoutId = setTimeout(() => {
      axios.put(`${API}/me/saved-views/appointments`, { views: savedViews }).catch(() => {
        // silent failure; next change will retry
      });
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [savedViews, viewsLoaded]);

  const defaultFilters = { dateFrom: '', dateTo: '', clientName: '', status: '', address: '', tagId: '', delivered: '', paid: '' };

  // Ensure filters have all keys
  const filters = { ...defaultFilters, ...appointmentFilters };

  const handleTagChange = async (appointmentId, newTagIds) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}/tags`, { tag_ids: newTagIds });
      setAppointments(prev => prev.map(a => a.id === appointmentId ? { ...a, tag_ids: newTagIds } : a));
    } catch (err) {
      showToast('Failed to update tags', 'error');
    }
  };

  const filteredAppointments = safeAppointments.filter(appointment => {
    if (filters.dateFrom && appointment.scheduled_date < filters.dateFrom) return false;
    if (filters.dateTo && appointment.scheduled_date > filters.dateTo) return false;
    if (filters.clientName) {
      const clientName = safeClients.find(c => c.id === appointment.client_id)?.name?.toLowerCase() || '';
      if (!clientName.includes(filters.clientName.toLowerCase())) return false;
    }
    if (filters.status && appointment.status !== filters.status) return false;
    if (filters.address) {
      const addr = (appointment.property_address || '').toLowerCase();
      if (!addr.includes(filters.address.toLowerCase())) return false;
    }
    if (filters.tagId) {
      const tagIds = appointment.tag_ids || [];
      if (!tagIds.includes(filters.tagId)) return false;
    }
    if (filters.delivered === 'yes' && !appointment.delivered) return false;
    if (filters.delivered === 'no' && appointment.delivered) return false;
    if (filters.paid === 'yes' && !appointment.paid) return false;
    if (filters.paid === 'no' && appointment.paid) return false;
    return true;
  });

  const hasActiveFilters = filters.dateFrom || filters.dateTo || filters.clientName || filters.status || filters.address || filters.tagId || filters.delivered || filters.paid;

  const handleSaveView = () => {
    if (!newViewName.trim()) return;
    const view = { id: Date.now().toString(), name: newViewName.trim(), filters: { ...filters } };
    setSavedViews(prev => [...prev, view]);
    setActiveViewId(view.id);
    setNewViewName('');
    setShowSaveViewInput(false);
    showToast(`View "${view.name}" saved`, 'success');
  };

  const handleLoadView = (view) => {
    setAppointmentFilters({ ...defaultFilters, ...view.filters });
    setActiveViewId(view.id);
    setShowAppointmentFilters(true);
  };

  const handleDeleteView = (viewId) => {
    setSavedViews(prev => prev.filter(v => v.id !== viewId));
    if (activeViewId === viewId) setActiveViewId(null);
    setConfirmDelete(null);
  };

  const handleDragStart = (e, viewId) => {
    setDraggedId(viewId);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires data to be set
    try { e.dataTransfer.setData('text/plain', viewId); } catch (err) { /* noop */ }
  };

  const handleDragOver = (e, viewId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedId && draggedId !== viewId) setDragOverId(viewId);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    setSavedViews(prev => {
      const from = prev.findIndex(v => v.id === draggedId);
      const to = prev.findIndex(v => v.id === targetId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const escapeCsv = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const handleExportCsv = () => {
    if (filteredAppointments.length === 0) {
      showToast('No appointments to export', 'info');
      return;
    }
    const headers = ['Date', 'Time', 'Client Name', 'Client Email', 'Property Address', 'Status', 'Delivered', 'Paid', 'Total Price', 'Tags'];
    const tagsById = Object.fromEntries(safeTags.map(t => [t.id, t.name]));
    const rows = filteredAppointments.map(a => {
      const client = safeClients.find(c => c.id === a.client_id);
      const tagNames = (a.tag_ids || []).map(id => tagsById[id]).filter(Boolean).join('; ');
      return [
        a.scheduled_date || '',
        a.scheduled_time || '',
        a.client_name || '',
        client?.email || '',
        a.property_address || '',
        a.status || '',
        a.delivered ? 'Yes' : 'No',
        a.paid ? 'Yes' : 'No',
        a.total_price ?? '',
        tagNames
      ].map(escapeCsv).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `appointments_${moment().format('YYYY-MM-DD_HHmm')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast(`Exported ${filteredAppointments.length} appointment${filteredAppointments.length === 1 ? '' : 's'}`, 'success');
  };

  const handleApproveSubmission = async (submission) => {
    try {
      await axios.put(`${API}/order-form-submissions/${submission.id}/approve`);
      setOrderFormSubmissions(prev => prev.map(s => s.id === submission.id ? { ...s, status: 'approved' } : s));
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` }});
      setAppointments(res.data);
      showToast('Booking approved!', 'success');
    } catch (err) {
      showToast('Failed to approve', 'error');
    }
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setAppointmentForm({
      client_id: appointment.client_id,
      client_ids: appointment.client_ids || [],
      service_type: appointment.service_type,
      property_address: appointment.property_address,
      scheduled_date: appointment.scheduled_date,
      scheduled_time: appointment.scheduled_time,
      duration_minutes: appointment.duration_minutes || 60,
      notes: appointment.notes || '',
      status: appointment.status,
      photographer_id: appointment.photographer_id || '',
      total_price: appointment.total_price || 0,
      selected_products: appointment.selected_products || []
    });
    setSelectedProducts(appointment.selected_products || []);
    setAppointmentClientSearchQuery(appointment.client_name || '');
    setShowAppointmentForm(true);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
      {/* Fixed-position Delete View Confirmation Popover */}
      {confirmDelete && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setConfirmDelete(null)}
            data-testid="delete-popover-backdrop"
          />
          <div
            className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 py-2.5 px-3 w-60 -translate-x-1/2"
            style={{ left: confirmDelete.x, top: confirmDelete.y }}
            data-testid="delete-view-confirmation"
          >
            <p className="text-xs text-gray-700 mb-2">
              Delete view <span className="font-semibold">"{confirmDelete.name}"</span>?
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 rounded"
                data-testid="cancel-delete-view"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteView(confirmDelete.id)}
                className="px-2.5 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded"
                data-testid="confirm-delete-view"
              >
                Delete
              </button>
            </div>
          </div>
        </>
      )}

      {/* Pending Booking Submissions */}
      {orderFormSubmissions.filter(s => s.status === 'pending_review').length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-amber-200 flex justify-between items-center bg-amber-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-amber-900">Pending Booking Requests</h3>
                <p className="text-sm text-amber-700">Review and approve before creating appointments</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-amber-500 text-white text-sm font-bold rounded-full">
              {orderFormSubmissions.filter(s => s.status === 'pending_review').length}
            </span>
          </div>
          <div className="p-4">
            <div className="space-y-2">
              {orderFormSubmissions.filter(s => s.status === 'pending_review').map((submission) => (
                <div key={submission.id} className="bg-white border border-amber-200 rounded-lg px-4 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-shrink-0" style={{width: '150px'}}>
                      <h4 className="font-medium text-gray-900 truncate text-sm">{submission.client_name}</h4>
                      <p className="text-xs text-gray-500 truncate">{submission.client_email}</p>
                    </div>
                    <div className="min-w-0 flex-1 hidden md:block">
                      <p className="text-sm text-gray-600 truncate">{submission.property_address || 'No address'}</p>
                    </div>
                    <div className="flex items-center gap-3 text-sm flex-shrink-0">
                      <div className="text-gray-600 whitespace-nowrap">
                        {submission.scheduled_date ? (
                          <>
                            <span className="font-medium">{moment(submission.scheduled_date).format('MMM D')}</span>
                            <span className="text-gray-400 mx-1">&bull;</span>
                            <span>{moment(submission.scheduled_time, 'HH:mm').format('h:mm A')}</span>
                          </>
                        ) : <span className="text-gray-400">No date</span>}
                      </div>
                      {submission.total_duration > 0 && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">
                          {submission.total_duration >= 60 
                            ? `${Math.floor(submission.total_duration / 60)}h${submission.total_duration % 60 > 0 ? ` ${submission.total_duration % 60}m` : ''}`
                            : `${submission.total_duration}m`}
                        </span>
                      )}
                      {submission.total_price > 0 && (
                        <span className="font-semibold text-green-600 whitespace-nowrap">${submission.total_price.toFixed(2)}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {submission.scheduled_date && (
                        <button 
                          onClick={() => { setCalendarMonth(new Date(submission.scheduled_date)); setCalendarView('week'); setActiveTab('calendar'); }}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                        >
                          View
                        </button>
                      )}
                      <button 
                        onClick={() => handleApproveSubmission(submission)}
                        className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg"
                        data-testid={`approve-submission-${submission.id}`}
                      >
                        Approve
                      </button>
                      <button 
                        onClick={() => { setSelectedPendingSubmission(submission); setShowRejectModal(true); setRejectReason(''); }}
                        className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
                      >
                        Reject
                      </button>
                      <button 
                        onClick={() => { setSelectedPendingSubmission(submission); setShowProposeTimeModal(true); setProposedDate(submission.scheduled_date || ''); setProposedTime(submission.scheduled_time || ''); setProposalNote(''); }}
                        className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg"
                      >
                        Propose
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Appointments</h2>
        {hasPermission('appointments', 'create') && (
          <button onClick={() => setShowAppointmentForm(true)} className="clara-btn-primary px-6 py-3" data-testid="add-appointment-btn">
            + New Appointment
          </button>
        )}
      </div>

      {/* Filter Section */}
      <div className="rounded-xl shadow-sm border border-gray-200 overflow-visible" style={{ backgroundColor: '#EFF6FF' }}>
        {/* Unified Header: Title + Saved View Tabs + Toggle */}
        <div className="flex items-stretch border-b border-blue-200/60">
          {/* Title acts as toggle */}
          <button
            onClick={() => setShowAppointmentFilters(!showAppointmentFilters)}
            className="flex items-center gap-2 px-4 py-3 hover:bg-blue-100/60 transition-colors flex-shrink-0 rounded-tl-xl"
            data-testid="filter-appointments-toggle"
          >
            <svg className={`w-4 h-4 text-gray-500 transform transition-transform ${showAppointmentFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <span className="font-medium text-gray-700 whitespace-nowrap">Filter Appointments</span>
            {hasActiveFilters && (
              <span className="text-[10px] font-semibold bg-blue-600 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wide">Active</span>
            )}
          </button>

          {/* Vertical divider */}
          <div className="w-px bg-blue-200/60 self-stretch" />

          {/* Inline Saved View Tabs - full-height, stretch to top of card */}
          <div className="flex items-stretch gap-0 flex-1 overflow-x-auto">
            <button
              onClick={() => { setActiveViewId(null); setAppointmentFilters(defaultFilters); }}
              className={`relative px-4 text-xs font-medium whitespace-nowrap transition-colors flex items-center ${
                !activeViewId
                  ? 'text-blue-700 bg-white'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-blue-100/50'
              }`}
              data-testid="saved-view-all"
            >
              All
              {!activeViewId && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
            </button>
            {savedViews.map(view => {
              const isActive = activeViewId === view.id;
              const isDragOver = dragOverId === view.id && draggedId !== view.id;
              const isDragging = draggedId === view.id;
              return (
                <div
                  key={view.id}
                  className={`group relative flex items-stretch ${isDragOver ? 'border-l-2 border-blue-500' : ''} ${isDragging ? 'opacity-50' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, view.id)}
                  onDragOver={(e) => handleDragOver(e, view.id)}
                  onDrop={(e) => handleDrop(e, view.id)}
                  onDragEnd={handleDragEnd}
                >
                  <button
                    onClick={() => handleLoadView(view)}
                    className={`relative px-4 pr-7 text-xs font-medium whitespace-nowrap transition-colors flex items-center cursor-grab active:cursor-grabbing ${
                      isActive
                        ? 'text-blue-700 bg-white'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-blue-100/50'
                    }`}
                    data-testid={`saved-view-${view.id}`}
                  >
                    {view.name}
                    {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setConfirmDelete({ id: view.id, name: view.name, x: rect.left + rect.width / 2, y: rect.bottom + 6 });
                    }}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete view"
                    data-testid={`delete-view-${view.id}`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {showAppointmentFilters && (
          <div className="p-4">
            {/* Row 1: Date, Client, Status */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input type="date" value={filters.dateFrom} onChange={(e) => setAppointmentFilters({...filters, dateFrom: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="filter-date-from" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input type="date" value={filters.dateTo} onChange={(e) => setAppointmentFilters({...filters, dateTo: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="filter-date-to" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <input type="text" placeholder="Client name..." value={filters.clientName} onChange={(e) => setAppointmentFilters({...filters, clientName: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="filter-client" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={filters.status} onChange={(e) => setAppointmentFilters({...filters, status: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="filter-status">
                  <option value="">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Row 2: Address, Tag, Delivered, Paid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input type="text" placeholder="Search address..." value={filters.address} onChange={(e) => setAppointmentFilters({...filters, address: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="filter-address" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag</label>
                <select value={filters.tagId} onChange={(e) => setAppointmentFilters({...filters, tagId: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="filter-tag">
                  <option value="">All Tags</option>
                  {[...safeTags].sort((a, b) => a.name.localeCompare(b.name)).map(tag => (
                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivered</label>
                <select value={filters.delivered} onChange={(e) => setAppointmentFilters({...filters, delivered: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="filter-delivered">
                  <option value="">Any</option>
                  <option value="yes">Delivered</option>
                  <option value="no">Not Delivered</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paid</label>
                <select value={filters.paid} onChange={(e) => setAppointmentFilters({...filters, paid: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="filter-paid">
                  <option value="">Any</option>
                  <option value="yes">Paid</option>
                  <option value="no">Unpaid</option>
                </select>
              </div>
            </div>

            {/* Actions: Clear Filters + Export CSV + Save View */}
            <div className="mt-4 flex items-center justify-end gap-2">
              <button 
                onClick={() => { setAppointmentFilters(defaultFilters); setActiveViewId(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="clear-filters-btn"
              >
                Clear Filters
              </button>
              <button
                onClick={handleExportCsv}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="export-csv-btn"
                title={`Export ${filteredAppointments.length} filtered appointment${filteredAppointments.length === 1 ? '' : 's'}`}
              >
                Export CSV <span className="text-gray-400">({filteredAppointments.length})</span>
              </button>
              {showSaveViewInput ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newViewName}
                    onChange={(e) => setNewViewName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveView()}
                    placeholder="View name..."
                    className="px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-40"
                    autoFocus
                    data-testid="save-view-name-input"
                  />
                  <button onClick={handleSaveView} className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors" data-testid="save-view-confirm-btn">
                    Save
                  </button>
                  <button onClick={() => { setShowSaveViewInput(false); setNewViewName(''); }} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700">
                    Cancel
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => setShowSaveViewInput(true)}
                  className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                  data-testid="save-view-btn"
                >
                  Save View
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Appointments Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="overflow-x-auto overflow-y-visible">
          <table className="clara-table min-w-full">
            <thead>
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Property</th>
                <th className="text-left px-4 py-3">Date & Time</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAppointments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No appointments found
                  </td>
                </tr>
              ) : (
                filteredAppointments.map(appointment => (
                  <React.Fragment key={appointment.id}>
                    <tr className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{appointment.client_name}</div>
                        <div className="text-xs text-gray-500">
                          {safeClients.find(c => c.id === appointment.client_id)?.email || ''}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 max-w-xs truncate">{appointment.property_address}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {moment(appointment.scheduled_date).format('MMM D, YYYY')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatTableTime(appointment.scheduled_time)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <StatusBadge status={appointment.status} />
                          <TagSelector
                            selectedTagIds={appointment.tag_ids || []}
                            availableTags={safeTags}
                            onChange={(newTagIds) => handleTagChange(appointment.id, newTagIds)}
                            compact={true}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleEditAppointment(appointment)}
                          className="clara-btn-secondary px-3 py-1 text-sm"
                          data-testid={`edit-appointment-${appointment.id}`}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {safeAppointments.length === 0 && (
        <div className="text-center py-12" data-testid="appointments-empty-state">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Appointments Yet</h3>
          <p className="text-gray-500 mb-4">Create your first appointment to get started</p>
          {hasPermission('appointments', 'create') && (
            <button onClick={() => setShowAppointmentForm(true)} className="clara-btn-primary px-6 py-3">
              + New Appointment
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AppointmentsPage;
